import Alert from '@amzn/awsui-components-core/alert';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Header from '@amzn/awsui-components-core/header';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-core/status-indicator';
import Table from '@amzn/awsui-components-core/table';
import Tiles from '@amzn/awsui-components-core/tiles';
import Input from '@amzn/awsui-components-core/input';
import React from 'react';
import { DataSyncText, DataSyncTextKey } from 'src/components/star-grid/hooks/useDataSyncText';
import { useDataSyncView } from 'src/components/star-grid/hooks/useDataSyncView';
import { DataSyncJob, DataSyncStatus } from 'src/components/star-grid/starGridTypes';
import { syncElapsedMs, syncMonthRange } from 'src/components/star-grid/utils/dataSync';
import { formatDurationMs, formatRowCount } from 'src/components/star-grid/utils/reportStatus';
import { getReadableDateTimeShortFormat } from 'src/utilities/DateTimeUtilities';
import styles from 'src/components/star-grid/views/styles/DataSyncView.module.scss';

const STATUS_DISPLAY: Record<DataSyncStatus, { type: StatusIndicatorProps.Type; label: DataSyncTextKey }> = {
  STARTING: { type: 'pending', label: 'starting' },
  RUNNING: { type: 'in-progress', label: 'running' },
  COMPLETED: { type: 'success', label: 'completed' },
  PARTIAL: { type: 'warning', label: 'partial' },
  FAILED: { type: 'error', label: 'failed' }
};

const SyncStatus: React.FC<{ status: DataSyncStatus; t: DataSyncText }> = ({ status, t }) => (
  <StatusIndicator type={STATUS_DISPLAY[status].type}>{t(STATUS_DISPLAY[status].label)}</StatusIndicator>
);

export const DataSyncView: React.FC = () => {
  const view = useDataSyncView();
  const { t, jobs, loading, error, submitting, activeJob, canTrigger } = view;

  return (
    <div className={styles.page}>
      <Header
        variant="h1"
        description={t('subtitle')}
        actions={
          <Button iconName="refresh" loading={loading} onClick={() => void view.refresh()}>
            {t('refresh')}
          </Button>
        }
      >
        {t('title')}
      </Header>

      {activeJob ? (
        <Alert type="info" header={t('activeTitle')}>
          <div className={styles.activeSummary}>
            <span>{t('activeMessage')}</span>
            <span className={styles.activeTime}>
              {t('job')} #{activeJob.id} · {t('totalTime')}: {formatDurationMs(syncElapsedMs(activeJob, view.now))}
            </span>
          </div>
        </Alert>
      ) : null}

      <section className={styles.triggerCard} aria-label={t('startTitle')}>
        <Header variant="h2">{t('startTitle')}</Header>
        <FormField label={t('scope')}>
          <Tiles
            value={view.scope}
            columns={2}
            onChange={({ detail }) => view.setScope(detail.value === 'FULL' ? 'FULL' : 'RANGE')}
            items={[
              { value: 'FULL', label: t('full'), description: t('fullDescription'), disabled: !!activeJob || submitting },
              { value: 'RANGE', label: t('range'), description: t('rangeDescription'), disabled: !!activeJob || submitting }
            ]}
          />
        </FormField>
        <p>{t('reloadNotice')}</p>
        {view.scope === 'RANGE' ? (
          <div className={styles.formRow}>
            <FormField label={t('scenario')} description={t('scenarioHelp')}>
              <Input
                value={view.scenario}
                onChange={({ detail }) => view.setScenario(detail.value)}
                inputMode="numeric"
                disabled={!!activeJob || submitting}
                placeholder="0"
              />
            </FormField>
            <FormField label={t('fromMonth')} description={t('inclusive')}>
              <Input
                value={view.fromMonth}
                onChange={({ detail }) => view.setFromMonth(detail.value)}
                inputMode="numeric"
                disabled={!!activeJob || submitting}
                placeholder="202601"
              />
            </FormField>
            <FormField label={t('toMonth')} description={t('inclusive')}>
              <Input
                value={view.toMonth}
                onChange={({ detail }) => view.setToMonth(detail.value)}
                inputMode="numeric"
                disabled={!!activeJob || submitting}
                placeholder="202612"
              />
            </FormField>
          </div>
        ) : null}
        {view.formError ? <Alert type="error">{view.formError}</Alert> : null}
        {error ? (
          <Alert type="error" header={t('checking')}>
            {t('checkingMessage')} {error}
          </Alert>
        ) : null}
        <div className={styles.formActions}>
          <Button
            variant="primary"
            iconName="refresh"
            loading={submitting}
            disabled={!canTrigger}
            data-testid="start-sync"
            onClick={() => void view.startSync()}
          >
            {t(view.scope === 'FULL' ? 'startFull' : 'startRange')}
          </Button>
        </div>
      </section>

      <Table<DataSyncJob>
        className={styles.historyTable}
        variant="container"
        items={jobs}
        trackBy="id"
        loading={loading && !jobs.length}
        loadingText={t('loading')}
        wrapLines
        header={
          <Header variant="h2" counter={`(${jobs.length})`} description={t('historyDescription')}>
            {t('history')}
          </Header>
        }
        empty={
          <div className={styles.empty}>
            <strong>{t('emptyTitle')}</strong>
            <p>{t('emptyDescription')}</p>
          </div>
        }
        columnDefinitions={[
          { id: 'id', header: t('job'), cell: (job) => `#${job.id}`, minWidth: 70 },
          { id: 'status', header: t('status'), cell: (job) => <SyncStatus status={job.status} t={t} />, minWidth: 160 },
          {
            id: 'scope',
            header: t('scope'),
            minWidth: 230,
            cell: (job) => (
              <div className={styles.scopeCell}>
                <strong>{t(job.sync_scope === 'FULL' ? 'full' : 'range')}</strong>
                <span>{job.sync_scope === 'FULL' || job.reporting_scenario_id === 0 ? t('allScenarios') : job.reporting_scenario_id}</span>
                <span>{job.sync_scope === 'FULL' ? t('allMonths') : syncMonthRange(job) ?? '—'}</span>
              </div>
            )
          },
          { id: 'rows', header: t('rows'), cell: (job) => <span className={styles.metric}>{formatRowCount(job.rows_synced)}</span>, minWidth: 140 },
          {
            id: 'total',
            header: t('totalTime'),
            cell: (job) => <span className={styles.metric}>{formatDurationMs(syncElapsedMs(job, view.now))}</span>,
            minWidth: 150
          },
          { id: 'started', header: t('started'), cell: (job) => getReadableDateTimeShortFormat(job.started_at ?? undefined) || '—', minWidth: 210 },
          { id: 'triggeredBy', header: t('triggeredBy'), cell: (job) => job.triggered_by ?? '—', minWidth: 130 },
          { id: 'error', header: t('details'), cell: (job) => <span className={styles.errorCell}>{job.error ?? '—'}</span>, minWidth: 180 }
        ]}
      />
    </div>
  );
};

export default DataSyncView;
